import React, { Children, useMemo } from 'react';

import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useScreen } from '@/hooks/useScreen';

export type FormGridDirection = 'row' | 'column';

interface FormGridContainerProps extends FormGridOptions {
  children: React.ReactNode;
}

const FORM_GRID_COLUMN_GAP = { singleRow: '16px', doubleRow: '32px' };
const FORM_GRID_ROW_GAP = { singleRow: '20px', doubleRow: '16px' };

export const FormGridContainer = ({
  children,
  numberOfFieldsInFirstColumn,
  direction = 'column',
  fullWidth = false,
  customBreakpoint,
  fieldVariant = FieldVariant.dataRow,
}: FormGridContainerProps) => {
  const { isMobile } = useScreen();
  const breakpoint = customBreakpoint !== undefined ? customBreakpoint : isMobile;

  const isFullWidth = breakpoint || fullWidth;

  const arrayOfChildren = useMemo(
    () => Children.toArray(children).filter((child) => React.isValidElement(child)),
    [children],
  );

  const numberOfChildren = arrayOfChildren.length;

  const leftColumnChildrenIndex = useMemo(
    () => Math.ceil(numberOfFieldsInFirstColumn ?? numberOfChildren / 2),
    [numberOfFieldsInFirstColumn, numberOfChildren],
  );

  const singleColumnStyle = {
    gridTemplateRows: '1fr',
    columnGap: FORM_GRID_COLUMN_GAP.singleRow,
    rowGap: fieldVariant === FieldVariant.form ? FORM_GRID_ROW_GAP.singleRow : '0px',
    gridAutoFlow: 'row',
  };

  const doubleColumnStyle = {
    gridTemplateColumns: 'repeat(2, 1fr)',
    columnGap: FORM_GRID_COLUMN_GAP.doubleRow,
    rowGap: fieldVariant === FieldVariant.form ? FORM_GRID_ROW_GAP.doubleRow : 0,
    gridAutoFlow: 'row',
  };

  const columnWrapperStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
  };

  if (direction === 'column' && !isFullWidth) {
    return (
      <div
        style={{
          display: 'grid',
          width: '100%',
          columnGap: FORM_GRID_COLUMN_GAP.doubleRow,
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}
      >
        <div style={columnWrapperStyle}>
          {arrayOfChildren.slice(0, leftColumnChildrenIndex).map((child, i) => (
            <React.Fragment key={i}>{child}</React.Fragment>
          ))}
        </div>
        <div style={columnWrapperStyle}>
          {arrayOfChildren.slice(leftColumnChildrenIndex).map((child, i) => (
            <React.Fragment key={i + leftColumnChildrenIndex}>{child}</React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        width: '100%',
        ...(isFullWidth ? singleColumnStyle : doubleColumnStyle),
      }}
    >
      {children}
    </div>
  );
};
